import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { SloganLine } from './SloganLine';

interface AboutOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const EASE: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export function AboutOverlay({ isOpen, onClose }: AboutOverlayProps) {
  // ESC key to close + body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <m.div
            className="fixed inset-0 z-[60] bg-black cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.95 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />

          {/* Content Panel */}
          <m.div
            className="fixed inset-0 z-[70] overflow-y-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: EASE }}
            role="dialog"
            aria-modal="true"
            aria-label="About Lightive"
          >
            <div className="min-h-screen flex items-center justify-center px-6 md:px-10 py-24 md:py-32">
              <div className="max-w-3xl w-full">
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="fixed top-6 right-6 md:top-8 md:right-10 font-en text-xs font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors z-[80] cursor-pointer"
                >
                  CLOSE
                </button>

                {/* Slogan */}
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, ease: EASE }}
                >
                  {/* Slogan */}
                  <SloganLine size="md" className="mb-10 md:mb-14" />

                  {/* Philosophy */}
                  <p className="font-kr text-base md:text-lg text-white/50 leading-relaxed mb-3">
                    빛은 공간을 바꾸고, 공간은 사람을 바꿉니다.
                  </p>

                  {/* Subtitle */}
                  <p className="font-en text-xs md:text-sm text-white/30 uppercase tracking-wider mb-1">
                    Spatial Media Content Company
                  </p>
                  <p className="font-en text-xs md:text-sm text-white/30 uppercase tracking-wider mb-12">
                    Busan, Korea &middot; Since 2018
                  </p>

                  {/* CTA to full About page */}
                  <Link
                    to="/about"
                    onClick={onClose}
                    className="inline-block font-en font-bold text-[11px] uppercase tracking-widest text-white border-2 border-white/20 px-8 py-3 hover:bg-white hover:text-black transition-all duration-200 cursor-pointer"
                  >
                    VIEW FULL ABOUT
                  </Link>
                </m.div>
              </div>
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  );
}
